import { IModel } from '@/@types/models/model';
import coreFields from './helpers/coreFields';

const listFields = [
  'id',
  'oznaka_plana',
  'oznaka_revizije',
  'naziv_plana',
  'mbr_jls',
  'zupanija_naziv',
  'vrsta_plana_id',
  'vrsta_postupka_id',
  'faza_id',
  'status_plana_id',
  'status_postupka_id',
  'zadnja_promjena',
  'strucni_izradivac',
  'datum_objave',
  'datum_stupanja_na_snagu',
  'odgovorna_organizacija',
  'poveznica',
  'izvan_snage',
  'nova_generacija',
];

// Read-only pp module registar (permission 703, GET /prostorni-planovi/registar) - same
// underlying pp.registar_prostornih_planova data and pp:registar.* field labels as the
// admin CRUD+import model (@/models/registar_prostornih_planova), but a distinct model
// since it points at a different (read-only, pp-permission-gated) endpoint.
//
// geom is inherited from the plan's JLS (a plan has no geometry of its own) - only the
// single-record GET returns it, used by the map-centric/data-centric pages to draw/zoom
// to the selected plan's municipality; layer matches the "Prostorni planovi (JLS)" WMS
// layer (gs_layer 'MAP:DEV_PP_registar') so map-click selection can resolve back to it.
const PPRegistar: IModel = {
  title: 'Registar prostornih planova',
  apiPath: 'prostorni-planovi/registar',
  layer: 'PP_registar',
  forms: {
    default: {
      fields: listFields,
    },
  },
  listViews: {
    default: {
      fields: listFields,
      // eKatalog-only fields - available via column picker but not cluttering the default
      // JLS/map-centric view, unlike the admin registar table where they're shown outright.
      hidden: ['id', 'datum_objave', 'datum_stupanja_na_snagu', 'odgovorna_organizacija', 'poveznica', 'izvan_snage'],
    },
  },
  fields: [
    coreFields.id,
    {
      title: 'oznaka_plana',
      source: 'oznaka_plana',
      ttoken: 'pp:registar.oznaka_plana',
      type: 'text',
      filter: true,
      readonly: true,
    },
    {
      title: 'oznaka_revizije',
      source: 'oznaka_revizije',
      ttoken: 'pp:registar.oznaka_revizije',
      type: 'text',
      filter: true,
      readonly: true,
    },
    {
      title: 'naziv_plana',
      source: 'naziv_plana',
      ttoken: 'pp:registar.naziv_plana',
      type: 'text',
      filter: true,
      readonly: true,
      width: 'full',
    },
    {
      title: 'mbr_jls',
      source: 'mbr_jls',
      ttoken: 'pp:registar.mbr_jls',
      type: 'numeric',
      filterType: 'text',
      filter: true,
      readonly: true,
    },
    {
      title: 'zupanija',
      source: 'zupanija_naziv',
      ttoken: 'pp:registar.zupanija',
      type: 'text',
      filter: true,
      readonly: true,
    },
    {
      title: 'vrsta_plana',
      source: 'vrsta_plana_id',
      ttoken: 'pp:registar.vrsta_plana',
      type: 'picker',
      subModel: {
        apiPath: 'prostorni-planovi/registar/vrste-plana',
        format: '{label}',
        idattr: 'id',
      },
      filter: true,
      readonly: true,
    },
    {
      title: 'vrsta_postupka',
      source: 'vrsta_postupka_id',
      ttoken: 'pp:registar.vrsta_postupka',
      type: 'picker',
      subModel: {
        apiPath: 'prostorni-planovi/registar/vrste-postupka',
        format: '{label}',
        idattr: 'id',
      },
      filter: true,
      readonly: true,
    },
    {
      title: 'faza',
      source: 'faza_id',
      ttoken: 'pp:registar.faza',
      type: 'picker',
      subModel: {
        apiPath: 'prostorni-planovi/registar/faze',
        format: '{label}',
        idattr: 'id',
      },
      filter: true,
      readonly: true,
    },
    {
      title: 'status_plana',
      source: 'status_plana_id',
      ttoken: 'pp:registar.status_plana',
      type: 'picker',
      subModel: {
        apiPath: 'prostorni-planovi/registar/statusi-plana',
        format: '{label}',
        idattr: 'id',
      },
      filter: true,
      readonly: true,
    },
    {
      title: 'status_postupka',
      source: 'status_postupka_id',
      ttoken: 'pp:registar.status_postupka',
      type: 'picker',
      subModel: {
        apiPath: 'prostorni-planovi/registar/statusi-postupka',
        format: '{label}',
        idattr: 'id',
      },
      filter: true,
      readonly: true,
    },
    {
      title: 'zadnja_promjena',
      source: 'zadnja_promjena',
      ttoken: 'pp:registar.zadnja_promjena',
      type: 'date',
      filter: true,
      readonly: true,
    },
    {
      title: 'strucni_izradivac',
      source: 'strucni_izradivac',
      ttoken: 'pp:registar.strucni_izradivac',
      type: 'text',
      filter: true,
      readonly: true,
    },
    {
      title: 'datum_objave',
      source: 'datum_objave',
      ttoken: 'pp:registar.datum_objave',
      type: 'date',
      filter: true,
      readonly: true,
    },
    {
      title: 'datum_stupanja_na_snagu',
      source: 'datum_stupanja_na_snagu',
      ttoken: 'pp:registar.datum_stupanja_na_snagu',
      type: 'date',
      filter: true,
      readonly: true,
    },
    {
      title: 'odgovorna_organizacija',
      source: 'odgovorna_organizacija',
      ttoken: 'pp:registar.odgovorna_organizacija',
      type: 'text',
      filter: true,
      readonly: true,
    },
    {
      title: 'poveznica',
      source: 'poveznica',
      ttoken: 'pp:registar.poveznica',
      type: 'text',
      filter: true,
      readonly: true,
    },
    {
      title: 'izvan_snage',
      source: 'izvan_snage',
      ttoken: 'pp:registar.izvan_snage',
      type: 'boolean',
      items: {
        labels: ['common.no', 'common.yes'],
        values: [false, true],
      },
      filter: true,
      readonly: true,
    },
    {
      title: 'nova_generacija',
      source: 'nova_generacija',
      ttoken: 'pp:registar.nova_generacija',
      type: 'boolean',
      items: {
        labels: ['common.no', 'common.yes'],
        values: [false, true],
      },
      filter: true,
      readonly: true,
    },
    coreFields.geom,
  ],
};

export default PPRegistar;
